import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCartCompany } from '~/hooks/cartCompany';
import IComplement from '~/interfaces/IComplement';
import IComplementItem from '~/interfaces/IComplementItem';
import IProduct from '~/interfaces/IProduct';
import { ICartProductPOS } from '~/interfaces/ICartProduct';
import { useCategories } from '~/hooks/categories';
import { useProductsCompany } from '../../../../hooks/productsCompany';
import {
  Container,
  Overlay,
  Details,
  ImageContainer,
  ComplementContainer,
  ComplementItem,
  QuantityContainer,
  TextareaContainer,
  DeleteButton,
  InputContainer,
  GroupTitle,
} from './styles';

const groupItemsByTitle = (items: IComplementItem[]) => {
  if (!items || !Array.isArray(items)) {
    return [];
  }
  
  const grouped = items.reduce((acc, item) => {
    const groupKey = item.groupTitle || 'Outros';
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(item);
    return acc;
  }, {} as Record<string, IComplementItem[]>);

  const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
    if (a === 'Outros') return 1;
    if (b === 'Outros') return -1;
    return a.localeCompare(b);
  });

  return sortedGroups;
};

const ProductDetails: React.FC = () => {
  const { selectProduct, productSelected } = useProductsCompany();
  const { add, productCart, remove, setProductCart, update } = useCartCompany();
  const { complements, categories } = useCategories();

  const [observations, setObservations] = useState('');
  const [quantity, setQuantity] = useState(1);

  const [complementsCount, setComplementsCount] = useState({} as any);
  const [complementsList, setComplementsList] = useState<IComplement[]>([]);

  const [search, setSearch] = useState('');

  const filteredComplements = useMemo(() => {
    const items = [] as IComplement[];

    complementsList.forEach(item => {
      const filtered = item.items.filter(comp =>
        comp.name.toLowerCase().includes(search.toLowerCase()),
      );
      items.push({
        ...item,
        items: filtered,
      });
    });

    return items;
  }, [complementsList, search]);

  useEffect(() => {
    console.log(productCart);
    if (productCart && productCart.uid) {
      const arrComplementsCount = {} as any;

      productCart.complements.forEach((item: any) => {
        arrComplementsCount[item.item_id] = item;
      });

      console.log(arrComplementsCount);

      setQuantity(productCart.quantity);
      setObservations(productCart.observations);
      setComplementsCount(arrComplementsCount);
    }
  }, [productCart]);

  useEffect(() => {
    const findCategory = categories.find(
      item => item.uid === productSelected.category.uid,
    );

    if (
      findCategory &&
      findCategory.complements &&
      findCategory.complements.length > 0
    ) {
      const complements_list = complements.filter(item =>
        findCategory.complements.includes(item.uid),
      );

      let list_complements = [] as IComplement[];
      if (productSelected.list_complements) {
        list_complements = productSelected.list_complements;
      }

      if (complements_list.length > 0) {
        list_complements = [...list_complements, ...complements_list];
      }

      setComplementsList(list_complements);
    } else {
      setComplementsList(
        productSelected.list_complements
          ? productSelected.list_complements
          : [],
      );
    }
  }, [complements, categories, productSelected]);

  const enabledButton = useMemo(() => {
    let result = true;

    productSelected.list_complements?.forEach(complement => {
      if (complement.type === '1') {
        let sum = 0;

        Object.entries(complementsCount).forEach(([key, value]) => {
          const item = value as any;

          if (item.group === complement.uid) {
            sum += item.quantity;
          }
        });

        if (sum < parseFloat(complement.min)) {
          result = false;
        }
      }
    });

    return result;
  }, [productSelected, complementsCount]);

  const total = useMemo(() => {
    let sum = parseFloat(
      productSelected.enable_promotion
        ? productSelected.value_promotion
        : productSelected.value,
    );

    // Check if any selected complement belongs to a pizza-type group
    const hasPizzaTypeComplement = Object.values(complementsCount).some((value: any) => {
      const complementGroup = complementsList.find(group => group.uid === value.group);
      return complementGroup?.pizzaType;
    });

    if (hasPizzaTypeComplement) {
      // For pizza-type groups, use the highest value among selected items
      Object.values(complementsCount).forEach((value: any) => {
        const complementGroup = complementsList.find(group => group.uid === value.group);
        if (complementGroup?.pizzaType) {
          const itemValue = parseFloat(value.item.value);
          if (itemValue > sum) sum = itemValue;
        } else {
          // For non-pizza groups, add normally
          sum += parseFloat(value.quantity) * parseFloat(value.item.value);
        }
      });
    } else {
      // Normal calculation for non-pizza products
      Object.values(complementsCount).forEach((value: any) => {
        sum += parseFloat(value.quantity) * parseFloat(value.item.value);
      });
    }

    return sum * quantity;
  }, [quantity, productSelected, complementsCount, complementsList]);

  const isRadioButton = useCallback((complement: IComplement) => {
    return (
      parseFloat(complement.min) === 1 &&
      parseFloat(complement.max) === 1 &&
      parseFloat(complement.type) === 1
    );
  }, []);

  const isQuantity = useCallback(
    (complement: IComplement) => {
      return parseFloat(complement.max) > 1 && !complement.pizzaType;
    },
    [],
  );

  const isCheckBox = useCallback(
    (complement: IComplement) => {
      return (
        complement.pizzaType ||
        (parseFloat(complement.max) === 1 &&
          parseFloat(complement.type) === 0)
      );
    },
    [],
  );

  const increment = useCallback(() => {
    setQuantity(current => current + 1);
  }, []);

  const decrement = useCallback(() => {
    const newQuantity = quantity - 1;

    if (productCart.uid && newQuantity >= 0) {
      setQuantity(newQuantity);
    } else if (newQuantity > 0) {
      setQuantity(newQuantity);
    }
  }, [quantity, productCart]);

  const toggleComplement = useCallback(
    (complement: IComplement, item: IComplementItem) => {
      const data = { ...complementsCount };

      // Check current count for this specific complement group
      let currentGroupCount = 0;
      Object.entries(data).forEach(([key, value]) => {
        const tmp = value as any;
        if (tmp.group === complement.uid) {
          currentGroupCount += tmp.quantity;
        }
      });

      if (data[item.uid]) {
        // Remove item if it exists
        delete data[item.uid];
        currentGroupCount -= 1;
      } else {
        // Add item if within max limit
        if (currentGroupCount < parseFloat(complement.max)) {
          data[item.uid] = {
            group: complement.uid,
            quantity: 1,
            item,
          };
          currentGroupCount += 1;
        }
      }

      setComplementsCount(data);
    },
    [complementsCount],
  );

  const incrementComplement = useCallback(
    (complement: IComplement, item: IComplementItem) => {
      console.log(item);

      const data = { ...complementsCount };

      console.log(complementsCount);

      let totalItemsByComplement = 0;

      Object.entries(data).forEach(([key, value]) => {
        const tmp = value as any;

        if (tmp.group === complement.uid) {
          totalItemsByComplement += tmp.quantity;
        }
      });

      console.log({ totalItemsByComplement });

      if (parseFloat(complement.max) > totalItemsByComplement) {
        if (data[item.uid]) {
          data[item.uid].quantity += 1;
        } else {
          data[item.uid] = {
            item,
            quantity: 1,
            group: complement.uid,
          };
        }

        setComplementsCount(data);
      }
    },
    [complementsCount],
  );

  const decrementComplement = useCallback(
    (complement: IComplement, item: IComplementItem) => {
      const data = { ...complementsCount };

      if (data[item.uid]) {
        const currentQuantity = data[item.uid].quantity;

        if (currentQuantity > 1) {
          data[item.uid].quantity -= 1;
        }

        if (currentQuantity === 1) {
          delete data[item.uid];
        }

        setComplementsCount(data);
      }
    },
    [complementsCount],
  );

  const addComplementItem = useCallback(
    (complement: IComplement, item: IComplementItem) => {
      const items = {
        ...complementsCount,
      };

      items[item.uid] = {
        // id: item.uid,
        // complement_id: complement.uid,
        // item_id: item.uid,
        // quantity: 1,
        // value: item.value || 0,
        // name: item.name,
        group: complement.uid,
        quantity: 1,
        item,
      };

      setComplementsCount(items);
    },
    [complementsCount],
  );

  const replaceComplementItem = useCallback(
    (complement_id: string, newItem: IComplementItem) => {
      const items = { ...complementsCount };

      Object.entries(items).forEach(([key, value]) => {
        const item = value as any;

        if (item.group === complement_id) {
          delete items[key];

          items[newItem.uid] = {
            // complement_id,
            // id: newItem.uid,
            // item_id: newItem.uid,
            // quantity: 1,
            // value: newItem.value,
            // name: newItem.name,
            group: complement_id,
            quantity: 1,
            item: newItem,
          };

          console.log(items);

          setComplementsCount(items);
        }
      });
    },
    [complementsCount],
  );

  const handleSelectComplement = useCallback(
    (complement: IComplement, item: IComplementItem) => {
      const items = { ...complementsCount };

      let totalItemsByComplement = 0;

      Object.entries(items).forEach(([key, value]) => {
        const tmp = value as any;

        if (tmp.group === complement.uid) {
          totalItemsByComplement += tmp.quantity;
        }
      });

      if (parseFloat(complement.max) > totalItemsByComplement) {
        addComplementItem(complement, item);
      }

      if (parseFloat(complement.max) === 1) {
        replaceComplementItem(complement.uid, item);
      }
    },
    [replaceComplementItem, complementsCount, addComplementItem],
  );

  const onClose = useCallback(() => {
    selectProduct({} as IProduct);
    setProductCart({} as ICartProductPOS);
  }, [selectProduct, setProductCart]);

  const handleSubmit = useCallback(() => {
    const { uid, name, value, image } = productSelected;

    const items = [] as any[];

    let valueTotal = parseFloat(
      productSelected.enable_promotion
        ? productSelected.value_promotion
        : value,
    );

    // Check if any selected complement belongs to a pizza-type group
    const hasPizzaTypeComplement = Object.values(complementsCount).some((value: any) => {
      const complementGroup = complementsList.find(group => group.uid === value.group);
      return complementGroup?.pizzaType;
    });

    if (hasPizzaTypeComplement) {
      // For pizza-type groups, use the highest value among selected items
      Object.values(complementsCount).forEach((value: any) => {
        const complementGroup = complementsList.find(group => group.uid === value.group);
        if (complementGroup?.pizzaType) {
          const itemValue = parseFloat(value.item.value);
          if (itemValue > valueTotal) valueTotal = itemValue;
        } else {
          // For non-pizza groups, add normally
          valueTotal += parseFloat(value.quantity) * parseFloat(value.item.value);
        }
      });
    } else {
      // Normal calculation for non-pizza products
      Object.values(complementsCount).forEach((value: any) => {
        valueTotal += parseFloat(value.quantity) * parseFloat(value.item.value);
      });
    }

    Object.entries(complementsCount).forEach(([key, values]) => {
      const valuesItem = values as any;
      const item = { ...valuesItem };

      items.push({
        ...item,
        item: {
          ...item.item,
          value: 0,
        },
      });
    });

    if (productCart.uid) {
      update({
        ...productCart,
        id: productCart.uid,
        product_id: uid,
        name,
        quantity,
        observations,
        image,
        value: `${valueTotal}`,
        complements: items,
      } as ICartProductPOS);
    } else {
      add({
        ...productSelected,
        uid,
        name,
        quantity,
        observations,
        image,
        value: `${valueTotal}`,
        complements: items,
      } as ICartProductPOS);
    }

    onClose();
  }, [
    quantity,
    add,
    productSelected,
    complementsCount,
    observations,
    productCart,
    update,
    onClose,
  ]);

  const handleRemoveItem = useCallback(() => {
    remove(productCart.uid);
    selectProduct({} as IProduct);
    setProductCart({} as ICartProductPOS);
  }, [productCart, remove, setProductCart, selectProduct]);

  return (
    <>
      <Container hasImage={!!productSelected.image}>
        <div>
          {productSelected.image && (
            <ImageContainer>
              <img src={productSelected.image} alt="Ilustracao" />
            </ImageContainer>
          )}

          <Details>
            <header>
              <strong>{productSelected.name}</strong>
              <button type="button" onClick={onClose} style={{ border: 0 }}>
                <i className="fa fa-times" />
              </button>
            </header>

            <main>
              {productSelected.description && (
                <p>{productSelected.description}</p>
              )}

              {productSelected.enable_promotion && (
                <p>
                  <span className="badge badge-success">Promoção</span>
                </p>
              )}
              <strong style={{ fontSize: 16 }}>
                {Number(
                  productSelected.enable_promotion
                    ? productSelected.value_promotion
                    : productSelected.value,
                ).toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </strong>
              {productSelected.enable_promotion && (
                <span
                  className="ml-1"
                  style={{ textDecoration: 'line-through' }}
                >
                  {Number(productSelected.value).toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </span>
              )}

              <hr />

              <InputContainer>
                <input
                  type="search"
                  placeholder="Buscar..."
                  className="mb-2"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </InputContainer>

              {filteredComplements.map(comp => (
                <ComplementContainer>
                  <header className="mb-2">
                    <div>
                      <strong>{comp.name}</strong>
                      <p>
                        {`quant. mínima: ${comp.min} | quant. máxima: ${comp.max}`}
                      </p>
                    </div>
                    {!!parseFloat(comp.type) && <span>Obrigatório</span>}
                  </header>

                  <main>
                    {groupItemsByTitle(comp.items).map(([groupTitle, groupItems]) => (
                      <div key={groupTitle}>
                        {groupTitle !== 'Outros' && (
                          <GroupTitle>{groupTitle}</GroupTitle>
                        )}
                        {groupItems.map(item => (
                          <ComplementItem
                            key={item.uid}
                            onClick={() => {
                              if (isRadioButton(comp)) {
                                handleSelectComplement(comp, item);
                              }
                            }}
                          >
                            <div>
                              <p>{item.name}</p>
                              {parseFloat(item.value) > 0 && (
                                <strong>
                                  {Number(item.value).toLocaleString('pt-br', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  })}
                                </strong>
                              )}
                            </div>

                            {isRadioButton(comp) && (
                              <input
                                type="radio"
                                name={comp.uid}
                                checked={complementsCount[item.uid]}
                              />
                            )}

                            {isCheckBox(comp) && (
                              <input
                                type="checkbox"
                                onChange={() => {
                                  toggleComplement(comp, item);
                                }}
                                checked={!!complementsCount[item.uid]}
                              />
                            )}

                            {isQuantity(comp) && (
                              <QuantityContainer>
                                <button
                                  type="button"
                                  onClick={() => decrementComplement(comp, item)}
                                >
                                  <em className="icon ni ni-minus" />
                                </button>
                                <input
                                  type="text"
                                  value={
                                    complementsCount[item.uid]
                                      ? complementsCount[item.uid].quantity
                                      : 0
                                  }
                                />
                                <button
                                  type="button"
                                  onClick={() => incrementComplement(comp, item)}
                                >
                                  <em className="icon ni ni-plus" />
                                </button>
                              </QuantityContainer>
                            )}
                          </ComplementItem>
                        ))}
                        {groupTitle !== 'Outros' && <div className="mb-3" />}
                      </div>
                    ))}
                    <div className="mb-2" />
                  </main>
                </ComplementContainer>
              ))}

              <TextareaContainer>
                <label htmlFor="observations">Observações:</label>
                <textarea
                  id="observations"
                  rows={5}
                  cols={10}
                  placeholder="Descrever observações..."
                  value={observations}
                  onChange={event => setObservations(event.target.value)}
                />
              </TextareaContainer>

              <div className="mb-5" />
            </main>

            <footer>
              <section>
                <button
                  type="button"
                  onClick={decrement}
                  disabled={quantity === 0}
                >
                  <em className="icon ni ni-minus" />
                </button>
                <input type="text" value={quantity} />
                <button type="button" onClick={increment}>
                  <em className="icon ni ni-plus" />
                </button>
              </section>

              {quantity > 0 && (
                <button
                  type="button"
                  disabled={!enabledButton}
                  onClick={handleSubmit}
                >
                  <strong>
                    {Number(total).toLocaleString('pt-br', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </strong>
                  <span>
                    {productCart && productCart.uid ? 'ATUALIZAR' : 'ADICIONAR'}
                  </span>
                </button>
              )}
              {quantity === 0 && (
                <DeleteButton onClick={handleRemoveItem}>
                  <span>REMOVER</span>
                </DeleteButton>
              )}
            </footer>
          </Details>
        </div>
      </Container>
      <Overlay onClick={onClose} />
    </>
  );
};

export default ProductDetails;
